import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { api } from '../../api/client';
import { useToast } from '../../context/ToastContext';
import '../../App.css';
import CourseForm, { ICourseFormData } from '../../components/forms/CourseForm';
import CoursePresentationForm, { ICoursePresentationData } from '../../components/forms/CoursePresentationForm';
import CourseScheduleWidget from '../../components/CourseScheduleWidget';
import CourseEnrollmentsWidget from '../../components/CourseEnrollmentsWidget';
import CourseMembershipOptionsWidget from '../../components/CourseMembershipOptionsWidget';
import CourseRenewalModal from '../../components/CourseRenewalModal';
import SeasonsSidebar from '../../components/courses/SeasonsSidebar';
import SeasonInfoPanel from '../../components/courses/SeasonInfoPanel';
import SeasonForm from '../../components/courses/SeasonForm';
import EditColumns from '../../components/EditColumns';
import Modal from '../../components/Modal';
import { ConfirmModal } from '../../components/ui';
import { useAuth } from '../../context/AuthContext';
import type { ICourseDetail, ISeason } from '../../api/client';
import { translateError } from '../../utils/errorMessages';

type ICourse = ICourseDetail;

type CourseTab = 'settings' | 'payments' | 'presentation' | 'schedule' | 'enrollments';

const CourseEditPage: React.FC = () => {
  const { t } = useTranslation();
  const { addToast } = useToast();
  const { hasPermission } = useAuth();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const canManage = hasPermission('activities:write');

  const [course, setCourse] = useState<ICourse | null>(null);
  const [seasons, setSeasons] = useState<ISeason[]>([]);
  const [selectedSeasonId, setSelectedSeasonId] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<CourseTab>('settings');
  const [renewOpen, setRenewOpen] = useState(false);
  const [seasonFormOpen, setSeasonFormOpen] = useState(false);
  // Term/price edits are easy to lose to a stray Escape.
  const [seasonFormDirty, setSeasonFormDirty] = useState(false);
  const [seasonDiscardOpen, setSeasonDiscardOpen] = useState(false);
  // Bumped on same-id renewal so the schedule/enrollments/payments widgets —
  // which fetch their own data keyed on the unchanging courseId — refetch live.
  const [renewCount, setRenewCount] = useState(0);

  const fetchCourse = useCallback(async () => {
    if (!id) return;
    try {
      const { data } = await api.courses.get(Number(id));
      setCourse(data as ICourse);
      setLoading(false);
    } catch (err: any) {
      setError(translateError(err, t));
      setLoading(false);
      if (err.response && err.response.status === 404) {
        navigate('/admin/courses');
      }
    }
  }, [id, navigate]);

  // Fetch every season of the course. Preserves the current selection when it
  // still exists; otherwise defaults to the active season (fallback: first).
  const fetchSeasons = useCallback(async (): Promise<ISeason[]> => {
    if (!id) return [];
    try {
      const { data } = await api.courses.seasons.list(Number(id));
      const list = data.seasons ?? [];
      setSeasons(list);
      setSelectedSeasonId((prev) => {
        if (prev != null && list.some((s) => s.id === prev)) return prev;
        const active = list.find((s) => s.isActive);
        return active ? active.id : list[0]?.id ?? null;
      });
      return list;
    } catch {
      return [];
    }
  }, [id]);

  useEffect(() => {
    fetchCourse();
    fetchSeasons();
  }, [fetchCourse, fetchSeasons]);

  // Refetch everything after any season mutation so all panels stay in sync.
  const handleSeasonChanged = useCallback(async () => {
    await fetchSeasons();
    await fetchCourse();
    setRenewCount((n) => n + 1);
  }, [fetchSeasons, fetchCourse]);

  // Edit is COURSE-LEVEL only — the season-owned fields (term/capacity/price/
  // policy/instructor) are edited on the selected season now, so they are
  // omitted here and from the update payload below.
  const initialFormData = useMemo<Partial<ICourseFormData> | undefined>(() => {
    if (!course) return undefined;
    return {
      name: course.name,
      description: course.description ?? '',
      isActive: course.isActive,
    };
  }, [course]);

  const selectedSeason = useMemo(
    () => seasons.find((s) => s.id === selectedSeasonId),
    [seasons, selectedSeasonId],
  );
  // Editability follows the season lifecycle, NOT `isActive` (the active season
  // can be a past fallback). Only current/upcoming seasons are editable.
  const canEditSelected =
    selectedSeason?.status === 'current' || selectedSeason?.status === 'upcoming';

  // The seasons picker widget column, shared by the Settings and Payments tabs
  // (the row selection is the single season selector — no separate dropdown).
  const seasonsColumn = id ? (
    <SeasonsSidebar
      courseId={Number(id)}
      seasons={seasons}
      selectedSeasonId={selectedSeasonId}
      onSelectSeason={setSelectedSeasonId}
      onEditSeason={(sid) => {
        setSelectedSeasonId(sid);
        setSeasonFormOpen(true);
      }}
      canManage={canManage}
      onPrepareNextSeason={() => setRenewOpen(true)}
      onSeasonChanged={handleSeasonChanged}
    />
  ) : null;

  const submitHandler = async (data: ICourseFormData) => {
    if (!id) return;
    setSaving(true);
    setError(null);
    try {
      // Course-level only. Season fields are updated via the selected season
      // (PUT /api/courses/:id/seasons/:seasonId), so they are not sent here.
      await api.courses.update(Number(id), {
        name: data.name,
        description: data.description,
        isActive: data.isActive,
      });
      addToast(t('courses.updated'), 'success');
      fetchCourse();
    } catch (err: any) {
      const errorMessage = translateError(err, t);
      setError(errorMessage);
      addToast(t('common.error') + ': ' + errorMessage, 'error');
    } finally {
      setSaving(false);
    }
  };

  const handlePresentationSave = async (data: ICoursePresentationData) => {
    if (!id) return;
    setSaving(true);
    setError(null);
    try {
      await api.courses.update(Number(id), {
        coverImage: data.coverImage || null,
        gallery: data.gallery,
        extendedDescription: data.extendedDescription || null,
        colorSettings: data.colorSettings,
      });
      addToast(t('courses.presentation.saved'), 'success');
      fetchCourse();
    } catch (err: any) {
      const errorMessage = translateError(err, t);
      setError(errorMessage);
      addToast(t('common.error') + ': ' + errorMessage, 'error');
    } finally {
      setSaving(false);
    }
  };

  const closeSeasonForm = () => {
    setSeasonFormOpen(false);
    setSeasonFormDirty(false);
    setSeasonDiscardOpen(false);
  };

  const guardSeasonFormClose = (): boolean | void => {
    if (!seasonFormDirty) return;
    setSeasonDiscardOpen(true);
    return false;
  };

  if (loading && !course) return <p>{t('common.loading')}</p>;
  if (error && !course) return <p className="error-message">{t('common.error')}: {error}</p>;

  return (
    <div className="admin-page-content course-edit-page">
      <h2 style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', flexWrap: 'wrap' }}>
        <span>{t('courses.edit')}{course ? `: ${course.name}` : ''}</span>
        {course?.isExpired && (
          <span className="course-edit-page__expired-badge">{t('courses.expired')}</span>
        )}
      </h2>

      <div className="tab-switcher" style={{ marginBottom: '1.5rem' }}>
        <button
          className={activeTab === 'settings' ? 'active' : ''}
          onClick={() => setActiveTab('settings')}
          type="button"
        >
          {t('courses.tabs.settings')}
        </button>
        <button
          className={activeTab === 'payments' ? 'active' : ''}
          onClick={() => setActiveTab('payments')}
          type="button"
        >
          {t('courses.tabs.payments')}
        </button>
        <button
          className={activeTab === 'presentation' ? 'active' : ''}
          onClick={() => setActiveTab('presentation')}
          type="button"
        >
          {t('courses.tabs.presentation')}
        </button>
        <button
          className={activeTab === 'schedule' ? 'active' : ''}
          onClick={() => setActiveTab('schedule')}
          type="button"
        >
          {t('courses.tabs.schedule')}
        </button>
        <button
          className={activeTab === 'enrollments' ? 'active' : ''}
          onClick={() => setActiveTab('enrollments')}
          type="button"
        >
          {t('courses.tabs.enrollments')}
        </button>
      </div>

      {activeTab === 'settings' && (
        <div className="tab-content active">
          {error && <p className="error-message">{t('common.error')}: {error}</p>}

          <EditColumns
            mainLabel={t('courses.tabs.settings')}
            sideLabel={t('courses.seasonsSidebar.title')}
            side={seasonsColumn}
          >
            <CourseForm
              mode="edit"
              initialData={initialFormData}
              seedKey={course?._id}
              onSubmit={submitHandler}
              loading={saving}
              submitLabel={t('common.save')}
            />
            <div className="course-edit-page__season-info">
              <SeasonInfoPanel season={selectedSeason} canEdit={canEditSelected} />
            </div>
          </EditColumns>
        </div>
      )}

      {activeTab === 'payments' && id && (
        <div className="tab-content active">
          <EditColumns
            mainLabel={t('courses.tabs.payments')}
            sideLabel={t('courses.seasonsSidebar.title')}
            side={seasonsColumn}
          >
            <CourseMembershipOptionsWidget
              courseId={Number(id)}
              seasonId={selectedSeasonId}
              canEdit={canEditSelected}
              termStart={selectedSeason?.termStart}
              termEnd={selectedSeason?.termEnd}
              refreshKey={renewCount}
              onChanged={handleSeasonChanged}
            />
          </EditColumns>
        </div>
      )}

      {activeTab === 'presentation' && (
        <div className="tab-content active form-section">
          {error && <p className="error-message">{t('common.error')}: {error}</p>}
          <CoursePresentationForm
            initialData={course ?? undefined}
            seedKey={course?._id}
            onSubmit={handlePresentationSave}
            loading={saving}
          />
        </div>
      )}

      {activeTab === 'schedule' && id && (
        <div className="tab-content active">
          <CourseScheduleWidget
            courseId={Number(id)}
            termStart={course?.termStart}
            termEnd={course?.termEnd}
            refreshKey={renewCount}
          />
        </div>
      )}

      {activeTab === 'enrollments' && id && (
        <div className="tab-content active">
          <CourseEnrollmentsWidget courseId={Number(id)} refreshKey={renewCount} />
        </div>
      )}

      {selectedSeason && id && (
        <Modal
          isOpen={seasonFormOpen}
          onClose={closeSeasonForm}
          onRequestClose={guardSeasonFormClose}
          title={t('courses.seasons.editTitle')}
          size="medium"
        >
          <SeasonForm
            courseId={Number(id)}
            season={selectedSeason}
            onDirtyChange={setSeasonFormDirty}
            onSaved={() => {
              closeSeasonForm();
              handleSeasonChanged();
            }}
            onCancel={closeSeasonForm}
          />
        </Modal>
      )}

      <ConfirmModal
        isOpen={seasonDiscardOpen}
        onClose={() => setSeasonDiscardOpen(false)}
        onConfirm={closeSeasonForm}
        title={t('unsavedChanges.title')}
        message={t('unsavedChanges.message')}
        confirmText={t('unsavedChanges.discard')}
        cancelText={t('unsavedChanges.keepEditing')}
        variant="danger"
      />

      {course && (
        <CourseRenewalModal
          isOpen={renewOpen}
          onClose={() => setRenewOpen(false)}
          course={course}
          onRenewed={async (newCourseId) => {
            setRenewOpen(false);
            // Renewal adds a new season under the SAME course id, so the backend
            // returns the current id. Only navigate if the id actually differs
            // (defensive, in case the backend ever mints a new course).
            if (String(newCourseId) !== id) {
              navigate(`/admin/courses/${newCourseId}/edit`);
              return;
            }
            // The renew result exposes only the course id, so after refetching
            // select the newest upcoming season (the one just prepared).
            const list = await fetchSeasons();
            await fetchCourse();
            setRenewCount((n) => n + 1);
            const upcoming = list.filter((s) => s.status === 'upcoming');
            const newest = (upcoming.length > 0 ? upcoming : list)
              .slice()
              .sort(
                (a, b) => (b.termStart ?? '').localeCompare(a.termStart ?? '') || b.id - a.id,
              )[0];
            if (newest) setSelectedSeasonId(newest.id);
          }}
        />
      )}

      <Link to="/admin/courses" className="form-link">{t('common.back')}</Link>
    </div>
  );
};

export default CourseEditPage;
